import { useCallback, useState } from 'react';

import { logError } from './logger';

const STORAGE_KEY_PREFIX = 'kai-renovator:';

export const readPersistedValue = <T>(key: string, defaultValue: T): T => {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_PREFIX + key);
    if (raw === null) return defaultValue;
    return JSON.parse(raw) as T;
  } catch (error) {
    logError(`usePersistedState: failed to read "${key}" from localStorage`, error);
    return defaultValue;
  }
};

export const writePersistedValue = <T>(key: string, value: T): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY_PREFIX + key, JSON.stringify(value));
  } catch (error) {
    logError(`usePersistedState: failed to write "${key}" to localStorage`, error);
  }
};

/**
 * State backed by localStorage, restored synchronously before first render —
 * a page that reads a filter through this hook renders with the user's last
 * selection already applied, and anything that fetches off that state (a
 * query param, a search string) fires once with the restored value instead
 * of once with a default and again once the real value loads.
 *
 * `key` is scoped under a shared prefix; keep it unique per filter per page,
 * e.g. `'contractors:search'`, `'dashboard:sortMode'`. The key is read once,
 * on mount — this hook doesn't react to a changing `key` prop.
 */
export const usePersistedState = <T>(
  key: string,
  defaultValue: T,
): [T, (value: T | ((previous: T) => T)) => void] => {
  const [value, setValue] = useState<T>(() => readPersistedValue(key, defaultValue));

  const setPersistedValue = useCallback((next: T | ((previous: T) => T)) => {
    setValue(previous => {
      const resolved = typeof next === 'function' ? (next as (previous: T) => T)(previous) : next;
      writePersistedValue(key, resolved);
      return resolved;
    });
  }, [key]);

  return [value, setPersistedValue];
};
